export const name = 'Notice'

import { ref } from 'vue'
import { useMainStore } from '@/stores/main'
import * as Server from '@/services/Server'
import * as Auth from '@/services/Auth'

/**
 * The one-off notice for new players ("you will lose at first..."), to be read at least once.
 *
 * The state lives here instead of inside a component because the trigger and the dialog are in
 * different places: more than one page can show the play button (see GamePlayButton), while the
 * dialog itself is mounted once in App.vue. Shared state is what keeps a second play button on the
 * page from opening a second dialog.
 *
 * Whether the notice was read is a fact of the account — it is stored server side, in
 * user_settings.notice_acknowledged_at — so it does not come back on another browser or after the
 * browser data is cleared.
 */
const open = ref(false)

// The account whose state has already been read: the first play button to mount asks the server,
// the ones mounted after it do nothing. Keyed on the id, because another player logging in on the
// same browser is a different account, not a state already read.
let readFor = null

export function isOpen() {
  return open.value
}

/**
 * Ask whether the notice is still to be shown and open the dialog if it is.
 * Called on mount by whatever shows a play button; calling it again does nothing.
 */
export function check() {
  const userId = Auth.getUserId()

  // Only an account that can actually play gets the notice, and that is the same condition that
  // makes the play button visible in the first place (see GamePlayButton).
  if (!userId || readFor === userId || !Auth.isVerified()) {
    return
  }

  readFor = userId
  const store = useMainStore()

  Server.get(store.apiPath.user_notice)
    .then(response => {
      // Strictly false: any other answer (a failed read included) means "do not show it". A notice
      // popping up on a call that went wrong is worse than one shown a session later.
      open.value = response.data?.data?.acknowledged === false
    })
    .catch(() => {
      // Neither read nor to read: the next play button to mount asks again.
      readFor = null
    })
}

/** The player read it: record it on the account, then close the dialog. */
export function acknowledge() {
  const store = useMainStore()
  open.value = false

  Server.put(store.apiPath.user_notice).catch(() => {
    // Not recorded: the notice will be shown again at the next session. Better than hiding it
    // forever on a call that never reached the server.
  })
}
